import { useEffect, useState } from 'react';
import { Loader2, MapPin, CheckCircle2, Check } from 'lucide-react';
import { doc, collection, serverTimestamp, GeoPoint } from 'firebase/firestore';
import Sheet from '../../shared/ui/Sheet';
import { Toggle, PrimaryButton } from '../../shared/ui';
import { db } from '../../shared/firebase';
import { updateRestaurant, updateUserProfile } from '../../shared/services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { RESTAURANT_TYPES, defaultCategories } from '../../shared/menuCategories';
import { SKN_BANKS, bankPickerValue } from '../../../../utils/banks';
import LocationPicker, { normalizeLng } from '../../../../components/LocationPicker';

// iOS Parish enum (Models.swift)
const PARISHES = [
  'Saint George Basseterre',
  'Saint Peter Basseterre',
  'Saint Anne Sandy Point',
  'Saint John Capisterre',
  'Christ Church Nichola Town',
  'Saint Paul Capisterre',
  'Saint Thomas Middle Island',
  'Saint Thomas Lowland',
  'Saint John Figtree',
  'Saint Paul Charlestown',
  'Saint George Gingerland',
  'Saint James Windward',
  "Saint Mary's Cayon",
];

// iOS Island enum (Island.swift)
const ISLANDS = ['St. Kitts', 'Nevis'];

const INPUT =
  'w-full px-3 py-2.5 rounded-xl bg-gray-100 text-sm outline-none focus:ring-2 focus:ring-[#FF6B35]/40';

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

const EMPTY = {
  name: '',
  description: '',
  address: '',
  parish: PARISHES[0],
  island: ISLANDS[0],
  phone: '',
  email: '',
  openingHours: 'Mon-Sun: 10:00 AM - 10:00 PM',
  openingTime: '10:00',
  closingTime: '22:00',
  restaurantType: 'Fast Food',
  menuCategories: [],
  isActive: true,
  bank: SKN_BANKS[0],
  bankOther: '',
  bankAccountName: '',
  bankAccountNumber: '',
  bankPhoneNumber: '',
};

// Mirrors iOS AddEditRestaurantView. When `restaurant` is null it creates a
// new restaurants/{id} doc and links it to the user profile.
export default function RestaurantFormSheet({ open, onClose, restaurant }) {
  const { user, refreshRestaurant } = useAuth();
  const isNew = !restaurant;
  const [form, setForm] = useState(EMPTY);
  const [coords, setCoords] = useState(null); // {latitude, longitude} from pin drop
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setShowLocationPicker(false);
    if (restaurant) {
      const raw = restaurant.raw || {};
      const c = raw.coordinates;
      setCoords(
        c && (c.latitude !== 0 || c.longitude !== 0)
          ? { latitude: c.latitude, longitude: normalizeLng(c.latitude, c.longitude) }
          : null
      );
      setForm({
        name: restaurant.name || '',
        description: restaurant.description || raw.description || '',
        address: restaurant.address || '',
        parish: restaurant.parish || PARISHES[0],
        island: restaurant.island || ISLANDS[0],
        phone: restaurant.phone || '',
        email: restaurant.email || user?.email || '',
        openingHours:
          raw.openingHours ||
          (restaurant.openingTime && restaurant.closingTime
            ? `${restaurant.openingTime} - ${restaurant.closingTime}`
            : EMPTY.openingHours),
        openingTime: toTimeInput(restaurant.openingTime) || '10:00',
        closingTime: toTimeInput(restaurant.closingTime) || '22:00',
        restaurantType: raw.restaurantType || 'Fast Food',
        menuCategories: raw.menuCategories || [],
        isActive: raw.isActive !== false,
        bank: bankPickerValue(raw.bankName || ''),
        bankOther: bankPickerValue(raw.bankName || '') === 'Other' ? raw.bankName || '' : '',
        bankAccountName: raw.bankAccountName || '',
        bankAccountNumber: raw.bankAccountNumber || '',
        bankPhoneNumber: raw.bankPhoneNumber || '',
      });
    } else {
      setCoords(null);
      setForm({ ...EMPTY, email: user?.email || '' });
    }
  }, [open, restaurant, user]);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  // iOS LocationPickerView.Confirm: writes the coordinate back and lets the
  // reverse-geocoded street/parish/island flow into the form fields.
  const onLocationPicked = ({ coordinates, street, parish, island }) => {
    if (coordinates) {
      setCoords({ latitude: coordinates.latitude, longitude: coordinates.longitude });
    }
    setForm((f) => ({
      ...f,
      address: street && street !== 'Pan the map to position the pin' ? street : f.address,
      parish: PARISHES.includes(parish) ? parish : f.parish,
      island: island === 'Nevis' ? 'Nevis' : 'St. Kitts',
    }));
    setShowLocationPicker(false);
  };

  const isValid =
    form.name.trim() &&
    form.address.trim() &&
    form.phone.trim() &&
    form.email.trim() &&
    form.email.includes('@');

  // iOS Menu Categories section — checkbox list built from
  // MenuCategory.defaultCategories(for: type). Saved categories that aren't
  // in the type's list are appended so they stay visible and removable.
  const categoryOptions = (() => {
    const opts = [...defaultCategories(form.restaurantType)];
    (restaurant?.raw?.menuCategories || []).forEach((c) => {
      if (c && !opts.includes(c)) opts.push(c);
    });
    return opts;
  })();

  const toggleCategory = (name) =>
    setForm((f) => ({
      ...f,
      menuCategories: f.menuCategories.includes(name)
        ? f.menuCategories.filter((c) => c !== name)
        : [...f.menuCategories, name],
    }));

  const onSave = async () => {
    if (!isValid || saving) return;
    if (!coords) {
      setError('Please drop a pin on the map to set your restaurant location.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const base = {
        name: form.name.trim(),
        description: form.description.trim(),
        address: form.address.trim(),
        parish: form.parish,
        island: form.island,
        phone: form.phone.trim(),
        phoneNumber: form.phone.trim(), // iOS field name
        email: form.email.trim().toLowerCase(),
        openingHours: form.openingHours.trim(),
        openingTime: form.openingTime,
        closingTime: form.closingTime,
        menuCategories: form.menuCategories,
        isActive: form.isActive,
        // Payout details — same merge-write contract as iOS
        // AddEditRestaurantViewModel ("Payout Details" section).
        bankName: form.bank === 'Other' ? form.bankOther.trim() : form.bank,
        bankAccountName: form.bankAccountName.trim(),
        bankAccountNumber: form.bankAccountNumber.trim(),
        bankPhoneNumber: form.bankPhoneNumber.trim(),
        // iOS AddEditRestaurantViewModel writes the picked GeoPoint on save
        coordinates: coords
          ? new GeoPoint(coords.latitude, normalizeLng(coords.latitude, coords.longitude))
          : new GeoPoint(0, 0),
      };

      if (isNew) {
        const newRef = doc(collection(db, 'restaurants'));
        await updateRestaurant(newRef.id, {
          ...base,
          menuImageURL: '',
          bannerURL: '',
          isVerified: false,
          isAcceptingOrders: true,
          isOpen: true,
          restaurantType: form.restaurantType,
          ownerId: user.uid,
          createdAt: serverTimestamp(),
        });
        await updateUserProfile(user.uid, { restaurantId: newRef.id });
      } else {
        await updateRestaurant(restaurant.id, base);
      }
      await refreshRestaurant();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Sheet
        open={open}
        onClose={onClose}
        title={isNew ? 'Set Up Store' : 'Edit Store'}
      >
      <div className="px-4 pb-6 space-y-4">
        <div>
          <p className="px-1 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Basic Information
          </p>
          <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-4">
            <Field label="Restaurant Name *">
              <input
                value={form.name}
                onChange={set('name')}
                placeholder="Restaurant name"
                className={INPUT}
              />
            </Field>
            <Field label="Description">
              <textarea
                value={form.description}
                onChange={set('description')}
                rows={3}
                placeholder="Tell customers about your restaurant"
                className={`${INPUT} resize-none`}
              />
            </Field>
            {isNew ? (
              <Field label="Restaurant Type">
                <select
                  value={form.restaurantType}
                  onChange={set('restaurantType')}
                  className={INPUT}
                >
                  {RESTAURANT_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Restaurant Type</span>
                <span className="font-medium text-gray-800">
                  {form.restaurantType}
                </span>
              </div>
            )}
            <div className="flex gap-3">
              <div className="flex-1">
                <Field label="Parish">
                  <select
                    value={form.parish}
                    onChange={set('parish')}
                    className={INPUT}
                  >
                    {PARISHES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="w-28">
                <Field label="Island">
                  <select
                    value={form.island}
                    onChange={set('island')}
                    className={INPUT}
                  >
                    {ISLANDS.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>
          </div>
        </div>

        <div>
          <p className="px-1 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Contact Information
          </p>
          <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-4">
            <Field label="Address *">
              <input
                value={form.address}
                onChange={set('address')}
                placeholder="Street address"
                className={INPUT}
              />
            </Field>
            {/* iOS "Set Location on Map" row — mappin icon + green check when set */}
            <button
              type="button"
              onClick={() => setShowLocationPicker(true)}
              className="w-full flex items-center gap-2.5 text-left py-1"
            >
              <MapPin size={18} className="text-[#FF6B35]" />
              <span className="text-sm font-medium text-gray-800">Set Location on Map</span>
              {coords && (
                <span className="ml-auto flex items-center gap-1.5">
                  <CheckCircle2 size={18} className="text-green-500" />
                  <span className="text-[11px] text-gray-400">
                    {coords.latitude.toFixed(4)}, {coords.longitude.toFixed(4)}
                  </span>
                </span>
              )}
            </button>
            <Field label="Phone Number *">
              <input
                value={form.phone}
                onChange={set('phone')}
                placeholder="+1 (869) ..."
                inputMode="tel"
                type="tel"
                className={INPUT}
              />
            </Field>
            {isNew ? (
              <Field label="Email *">
                <input
                  value={form.email}
                  onChange={set('email')}
                  placeholder="restaurant@email.com"
                  inputMode="email"
                  type="email"
                  className={INPUT}
                />
              </Field>
            ) : (
              <div className="flex items-center justify-between text-sm gap-3">
                <span className="text-gray-500 shrink-0">Email</span>
                <span className="font-medium text-gray-800 truncate">
                  {form.email}
                </span>
              </div>
            )}
            <Field label="Opening Hours">
              <input
                value={form.openingHours}
                onChange={set('openingHours')}
                placeholder="Mon-Sun: 10:00 AM - 10:00 PM"
                className={INPUT}
              />
            </Field>
            <div className="flex gap-3">
              <div className="flex-1">
                <Field label="Opens">
                  <input
                    type="time"
                    value={form.openingTime}
                    onChange={set('openingTime')}
                    className={INPUT}
                  />
                </Field>
              </div>
              <div className="flex-1">
                <Field label="Closes">
                  <input
                    type="time"
                    value={form.closingTime}
                    onChange={set('closingTime')}
                    className={INPUT}
                  />
                </Field>
              </div>
            </div>
          </div>
        </div>

        <div>
          <p className="px-1 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Menu Categories
          </p>
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
              {categoryOptions.map((c) => {
                const checked = form.menuCategories.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCategory(c)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left active:bg-gray-50"
                  >
                    <span
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
                        checked
                          ? 'bg-[#FF6B35] border-[#FF6B35]'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {checked && <Check size={13} className="text-white" strokeWidth={3.5} />}
                    </span>
                    <span className="text-sm text-gray-800">{c}</span>
                  </button>
                );
              })}
            </div>
            <p className="px-4 py-2.5 text-[11px] text-gray-400 border-t border-gray-100">
              Select all categories that apply to your menu
            </p>
          </div>
        </div>

        <div>
          <p className="px-1 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Status
          </p>
          <div className="bg-white rounded-2xl border border-gray-100">
            <div className="px-4 py-3 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-800">Active</span>
              <Toggle checked={form.isActive} onChange={set('isActive')} />
            </div>
          </div>
        </div>

        {/* iOS "Payout Details" section — bank transfer destination */}
        <div>
          <p className="px-1 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Payout Details
          </p>
          <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-4">
            <Field label="Bank">
              <select value={form.bank} onChange={set('bank')} className={INPUT}>
                {SKN_BANKS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </Field>
            {form.bank === 'Other' && (
              <Field label="Bank Name">
                <input
                  value={form.bankOther}
                  onChange={set('bankOther')}
                  placeholder="Bank or credit union name"
                  className={INPUT}
                />
              </Field>
            )}
            <Field label="Account Name">
              <input
                value={form.bankAccountName}
                onChange={set('bankAccountName')}
                placeholder="Name on the account"
                className={INPUT}
              />
            </Field>
            <Field label="Account Number">
              <input
                value={form.bankAccountNumber}
                onChange={set('bankAccountNumber')}
                placeholder="Account number"
                inputMode="numeric"
                className={INPUT}
              />
            </Field>
            <Field label="Bank Phone Number">
              <input
                value={form.bankPhoneNumber}
                onChange={set('bankPhoneNumber')}
                placeholder="(869) 555-1234"
                inputMode="tel"
                type="tel"
                className={INPUT}
              />
            </Field>
            <p className="text-[11px] text-gray-400">
              Payments are made outside the app by bank transfer. The team uses
              these details to pay the business — check the payout calendar on
              the Payments tab.
            </p>
          </div>
        </div>

        {error && (
          <div className="px-3 py-2 rounded-xl bg-red-50 text-red-600 text-xs font-medium">
            {error}
          </div>
        )}
        {!isValid && !error && (
          <p className="text-xs text-orange-600 px-1">
            Name, address, phone, and a valid email are required.
          </p>
        )}

        <PrimaryButton onClick={onSave} disabled={!isValid || saving}>
          {saving ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              Saving...
            </span>
          ) : isNew ? (
            'Create Store'
          ) : (
            'Save'
          )}
        </PrimaryButton>
      </div>
      </Sheet>

      {/* iOS LocationPickerView — full-screen pin drop over the sheet */}
      {showLocationPicker && (
        <LocationPicker
          initialCoordinates={coords || undefined}
          onLocationSelect={onLocationPicked}
          onClose={() => setShowLocationPicker(false)}
        />
      )}
    </>
  );
}

// "10:00" / "10:00 AM" / Firestore-ish strings -> HH:MM for <input type="time">
function toTimeInput(v) {
  if (!v || typeof v !== 'string') return '';
  const m = v.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return '';
  let h = parseInt(m[1], 10);
  const min = m[2];
  const mer = m[3]?.toUpperCase();
  if (mer === 'PM' && h < 12) h += 12;
  if (mer === 'AM' && h === 12) h = 0;
  if (h > 23) return '';
  return `${String(h).padStart(2, '0')}:${min}`;
}
